import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Clock, Phone, Mail, CalendarDays } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase/config";
import { formatCurrency, formatDate } from "@/lib/utils/formatters";
import type { Booking } from "@/types/booking";

const MAX_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

export default function BookingConfirmationPage() {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("id");
  const bookingRef = searchParams.get("ref");

  const [booking, setBooking] = useState<Booking | null>(null);
  const [status, setStatus] = useState<"polling" | "confirmed" | "timeout">(
    "polling",
  );
  const attemptRef = useRef(0);

  useEffect(() => {
    if (!bookingId) {
      setStatus("timeout");
      return;
    }

    const poll = async () => {
      attemptRef.current += 1;

      try {
        const snap = await getDoc(doc(db, "bookings", bookingId));
        if (snap.exists()) {
          const data = { id: snap.id, ...snap.data() } as Booking;
          if (data.status === "confirmed" || data.status === "checked_in") {
            setBooking(data);
            setStatus("confirmed");
            return;
          }
        }
      } catch (err) {
        console.error("Polling error:", err);
      }

      if (attemptRef.current < MAX_ATTEMPTS) {
        setTimeout(poll, POLL_INTERVAL_MS);
      } else {
        try {
          const snap = await getDoc(doc(db, "bookings", bookingId));
          if (snap.exists()) {
            setBooking({ id: snap.id, ...snap.data() } as Booking);
          }
        } catch {}
        setStatus("timeout");
      }
    };

    poll();
  }, [bookingId]);

  if (status === "polling") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-metro-cream px-4 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-metro-green-50">
          <Clock size={28} className="animate-pulse text-metro-green-800" />
        </div>
        <h2 className="font-serif text-2xl text-metro-green-950">
          Confirming your booking…
        </h2>
        <p className="max-w-sm text-sm text-metro-ink-muted">
          Please wait while we verify your payment. This usually takes just a
          moment.
        </p>
        <div className="mt-2 flex gap-1.5">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-2 w-2 rounded-full bg-metro-green-800 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (status === "timeout" && !booking) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-metro-cream px-4 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-50">
          <Clock size={28} className="text-amber-500" />
        </div>
        <h2 className="font-serif text-2xl text-metro-green-950">
          Payment received — confirmation coming
        </h2>
        <p className="max-w-sm text-sm text-metro-ink-muted">
          Your payment was successful. The booking confirmation may take a
          minute to process. You'll receive a confirmation email shortly.
        </p>
        {bookingRef && (
          <p className="rounded-lg bg-white px-4 py-2 font-mono text-sm font-semibold text-metro-green-800 shadow-sm">
            Ref: {bookingRef}
          </p>
        )}
        <div className="flex flex-col items-center gap-2 text-sm text-metro-ink-muted mt-2">
          <a
            href="tel:+919906510627"
            className="flex items-center gap-1.5 font-medium text-metro-green-800 hover:underline"
          >
            <Phone size={13} /> +91 99065 10627
          </a>
          <a
            href="mailto:metropoliskmr1@gmail.com"
            className="flex items-center gap-1.5 font-medium text-metro-green-800 hover:underline"
          >
            <Mail size={13} /> metropoliskmr1@gmail.com
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-metro-cream">
      <div className="border-b border-gray-100 bg-white">
        <div className="container-narrow py-4">
          <p className="font-serif text-xl tracking-widest text-metro-green-950">
            METROPOLIS
          </p>
        </div>
      </div>

      <div className="container-narrow py-16">
        <div className="mx-auto max-w-2xl">
          <div className="rounded-2xl bg-white p-8 shadow-card text-center md:p-12">
            <div className="mb-6 flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-metro-green-50">
                <CheckCircle
                  size={36}
                  className="text-metro-green-800"
                  strokeWidth={1.5}
                />
              </div>
            </div>

            <h1 className="mb-2 font-serif text-3xl text-metro-green-950">
              Booking Confirmed!
            </h1>
            <p className="mb-1 text-metro-ink-muted">
              Thank you for choosing Hotel Metropolis.
            </p>
            <p className="mb-8 text-sm text-metro-ink-subtle">
              Your booking reference is{" "}
              <span className="font-mono font-semibold text-metro-green-800">
                {booking?.bookingRef ?? bookingRef ?? "—"}
              </span>
            </p>

            {booking && (
              <div className="mb-8 rounded-xl border border-metro-cream-100 bg-metro-cream p-6 text-left space-y-4">
                <div className="grid gap-4 sm:grid-cols-2 text-sm">
                  <div>
                    <p className="text-xs text-metro-ink-subtle mb-0.5">Room</p>
                    <p className="font-medium text-metro-green-950">
                      {booking.roomTypeName}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-metro-ink-subtle mb-0.5">
                      Guests
                    </p>
                    <p className="font-medium text-metro-green-950">
                      {booking.guests.adults}{" "}
                      {booking.guests.adults === 1 ? "Adult" : "Adults"}
                      {booking.guests.children > 0 &&
                        `, ${booking.guests.children} ${booking.guests.children === 1 ? "Child" : "Children"}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarDays size={14} className="text-metro-green-800" />
                    <div>
                      <p className="text-xs text-metro-ink-subtle mb-0.5">
                        Check-in
                      </p>
                      <p className="font-medium text-metro-green-950">
                        {formatDate(booking.checkIn)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarDays size={14} className="text-metro-green-800" />
                    <div>
                      <p className="text-xs text-metro-ink-subtle mb-0.5">
                        Check-out
                      </p>
                      <p className="font-medium text-metro-green-950">
                        {formatDate(booking.checkOut)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-metro-cream-100 pt-4 space-y-1.5 text-sm">
                  <div className="flex justify-between text-metro-ink-muted">
                    <span>
                      {formatCurrency(booking.pricing.basePrice)} ×{" "}
                      {booking.pricing.nights} nights
                    </span>
                    <span>{formatCurrency(booking.pricing.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-metro-ink-muted">
                    <span>GST ({booking.pricing.gstRate}%)</span>
                    <span>{formatCurrency(booking.pricing.gstAmount)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-metro-green-950 border-t border-metro-cream-100 pt-2">
                    <span>Total Paid</span>
                    <span>{formatCurrency(booking.pricing.total)}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2 text-sm text-metro-ink-muted">
              <p>Need help with your booking?</p>
              <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
                <a
                  href="tel:+919906510627"
                  className="flex items-center gap-1.5 font-medium text-metro-green-800 hover:underline"
                >
                  <Phone size={14} />
                  +91 99065 10627
                </a>
                <span className="hidden sm:inline text-metro-ink-subtle">
                  ·
                </span>
                <a
                  href="mailto:metropoliskmr1@gmail.com"
                  className="flex items-center gap-1.5 font-medium text-metro-green-800 hover:underline"
                >
                  <Mail size={14} />
                  metropoliskmr1@gmail.com
                </a>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col items-center gap-3 text-center">
            <Link
              to="/booking/lookup"
              className="text-sm font-medium text-metro-green-800 underline-offset-2 hover:underline"
            >
              View or manage your booking →
            </Link>
            <Link
              to="/"
              className="text-sm font-medium text-metro-ink-subtle underline-offset-2 hover:underline"
            >
              ← Back to Hotel Metropolis
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}